// ============================================
// SERVICE: QUEIXAS
// ============================================
// Funções para gerenciar queixas via API
// ============================================

import { API_CONFIG } from './staticDataConfig';

const baseURL = API_CONFIG.BASE_URL;

export interface Queixa {
  id: number;
  valor: string;
  descricao?: string;
  categoria?: string;
  ativo: boolean;
  ordem: number;
}

class QueixasService {
  private getToken(): string | null {
    return sessionStorage.getItem('token');
  }

  private getHeaders(): HeadersInit {
    const token = this.getToken();
    return {
      'Content-Type': 'application/json',
      ...(token && { 'Authorization': `Bearer ${token}` })
    };
  }

  // Buscar todas as queixas (ativas ou incluindo inativas)
  async obterTodos(incluirInativos: boolean = false): Promise<Queixa[]> {
    try {
      const url = incluirInativos 
        ? `${baseURL}/queixas?incluirInativos=true`
        : `${baseURL}/queixas`;
      
      const response = await fetch(url, {
        method: 'GET',
        headers: this.getHeaders()
      });

      if (!response.ok) {
        throw new Error(`Erro ao buscar queixas: ${response.statusText}`);
      }

      const data = await response.json();
      // console.log(`[QueixasService] Queixas obtidas: ${data.queixas?.length || 0} (incluirInativos: ${incluirInativos})`);
      return data.queixas || [];
    } catch (error) {
      console.error('[QueixasService] Erro ao obter queixas:', error);
      throw error;
    }
  }

  // Adicionar nova queixa
  async adicionar(queixa: {
    valor: string;
    descricao?: string;
    categoria?: string;
    ordem?: number;
  }): Promise<Queixa> {
    try {
      const response = await fetch(`${baseURL}/queixas`, {
        method: 'POST',
        headers: this.getHeaders(),
        body: JSON.stringify(queixa)
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.mensagem || 'Erro ao adicionar queixa');
      }

      const data = await response.json();
      return data.queixa;
    } catch (error) {
      console.error('[QueixasService] Erro ao adicionar queixa:', error);
      throw error;
    }
  }

  // Atualizar queixa existente
  async atualizar(id: number, queixa: {
    valor?: string;
    descricao?: string;
    categoria?: string;
    ordem?: number;
    ativo?: boolean;
  }): Promise<Queixa> {
    try {
      const response = await fetch(`${baseURL}/queixas/${id}`, {
        method: 'PUT',
        headers: this.getHeaders(),
        body: JSON.stringify(queixa)
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.mensagem || 'Erro ao atualizar queixa');
      }

      const data = await response.json();
      return data.queixa;
    } catch (error) {
      console.error('[QueixasService] Erro ao atualizar queixa:', error);
      throw error;
    }
  }

  // Excluir queixa (proteção inteligente)
  async excluir(id: number, forcarDelete: boolean = false): Promise<any> {
    try {
      const url = forcarDelete 
        ? `${baseURL}/queixas/${id}?forcarDelete=true`
        : `${baseURL}/queixas/${id}`;
        
      const response = await fetch(url, {
        method: 'DELETE',
        headers: this.getHeaders()
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.mensagem || 'Erro ao excluir queixa');
      }
      
      return await response.json();
    } catch (error) {
      console.error('[QueixasService] Erro ao excluir queixa:', error);
      throw error;
    }
  }
}

export const queixasService = new QueixasService();
